import React, { useCallback, useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { Field, formValueSelector, reduxForm } from "redux-form";
import { FormattedMessage } from "react-intl";
import cx from "classnames"
import FormGroup from "react-bootstrap/FormGroup";
import withStyles from "isomorphic-style-loader/withStyles";
import messages from "../../../locale/messages";
import PlaceGeoSuggest from "../../Common/PlaceGeoSuggest/PlaceGeoSuggest";
import bt from '../../commonStyle.css';
import s from './LocationSearchForm.css';

function LocationSearchForm(props) {
    const { pricingType, intl: { formatMessage }, change, location, profileLocation } = props

    // Infer the location instead of re-asking (app parity with showServiceAddress): if the customer
    // already has a saved profile location (preferredLocation + lat/lng), pre-fill it and show it
    // READ-ONLY with a "Change" link, rather than forcing a fresh geosuggest pick. The real geosuggest
    // (the only thing that produces valid lat/lng for pricing) stays behind "Change". When there's NO
    // saved address, we fall back to the picker exactly as before.
    const [showPicker, setShowPicker] = useState(false)
    const [prefilled, setPrefilled] = useState(false)

    const hasProfileLocation =
        profileLocation && profileLocation.lat != null && profileLocation.lng != null

    // Pre-fill from the profile once, only when the form has no location yet (don't clobber a manual
    // pick or a restored value, and don't re-fire after the user opens the picker).
    useEffect(() => {
        if (prefilled) return
        if (location?.location?.lat && location?.location?.lng) return
        if (!hasProfileLocation) return
        change('location', {
            location: {
                label: profileLocation.label,
                lat: profileLocation.lat,
                lng: profileLocation.lng,
            },
        })
        setPrefilled(true)
    }, [hasProfileLocation, location, prefilled]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleSelectSuggest = useCallback(async (name, data, value) => {
        let location = {
            // Prefer Google's formattedAddress (US results include the ZIP) so the stored address
            // carries the ZIP — the worker pre-accept view derives "City, State ZIP" from it.
            label: data?.formattedAddress || value,
            lat: data?.location?.latitude,
            lng: data?.location?.longitude,
        }
        await change(name, { location } || '')
    })
    const renderPlacesSuggest = useCallback(({ input, id, label, meta: { error }, initialValue, isTouched, disabled, placeholder }) => {
        return (
            <FormGroup className={'mb0'}>
                <label className={bt.commonLabelText}>{label}</label>
                <PlaceGeoSuggest
                    {...input}
                    label={label}
                    placeholder={placeholder}
                    handleSelectSuggest={(data, value) => handleSelectSuggest(input.name, data || '', value)}
                    disabled={disabled}
                    initialValue={initialValue}
                    id={id}
                    inputClass={cx(bt.formControlInput, 'form-control')}
                    containerClassName="categoryLocationSearchContainer"
                />
                {isTouched && error && <span>{formatMessage(error)}</span>}
            </FormGroup>
        )
    }, []);

    // Show the read-only summary when we have a valid location AND the user hasn't asked to change it.
    const currentLabel = location?.location?.label
    const hasValidLocation = location?.location?.lat != null && location?.location?.lng != null
    const showReadOnly = hasValidLocation && currentLabel && !showPicker

    return (
        <>
            {
                (pricingType === 'hourly' || pricingType === 'fixed') && (
                    <div>
                        <div className={s.viewCategoryHourlySearchHeading}>
                            <FormattedMessage {...messages.chooseLocation} />
                        </div>
                        {
                            showReadOnly ? (
                                <FormGroup className={'mb0'}>
                                    <label className={bt.commonLabelText}>{formatMessage(messages.yourLocationText)}</label>
                                    <div className={s.inferredLocationRow}>
                                        <span className={s.inferredLocationLabel}>{currentLabel}</span>
                                        <button
                                            type="button"
                                            className={s.changeLocationLink}
                                            onClick={() => setShowPicker(true)}
                                        >
                                            <FormattedMessage {...messages.changeLocationLink} />
                                        </button>
                                    </div>
                                </FormGroup>
                            ) : (
                                <Field
                                    name="location"
                                    type="text"
                                    component={renderPlacesSuggest}
                                    label={formatMessage(messages.yourLocationText)}
                                    placeholder={formatMessage(messages.locationLabel)}
                                    disabled={false}
                                    autoComplete='off'
                                    id={`geo_suggest_pickUp`}
                                />
                            )
                        }
                    </div>
                )
            }
        </>
    )

}
LocationSearchForm = reduxForm({
    form: 'LocationSearchForm',
})(LocationSearchForm);

const selector = formValueSelector("LocationSearchForm")
const mapState = (state) => {
    const account = state?.userAccount?.data
    return {
        pricingType: selector(state, 'pricingType') || '',
        location: selector(state, 'location'),
        // Saved profile location used to pre-fill the picker (app parity). Only usable when both the
        // label and real coordinates exist (lat/lng must stay valid for pricing).
        profileLocation: account && account.lat != null && account.lng != null
            ? { label: account.preferredLocation || account.address, lat: account.lat, lng: account.lng }
            : null,
    }
}

export default injectIntl(withStyles(bt, s)(connect(mapState, {})(LocationSearchForm)))
